import { useEffect, useRef, useState } from 'react';
import type { AudioTrack } from '@shared/types.ts';
import { player } from '@/lib/player.ts';
import { usePlayer } from '@/stores/playback.ts';

/**
 * AUDIO TRACK AND SPEED, as a desk player has them.
 *
 * Deliberately NOT a port of the television's OSD. That screen is panels the
 * size of a hand because it is read from a sofa and driven by a D-pad; here
 * there is a pointer, eighteen inches of viewing distance and a bar already
 * on screen. Same capabilities, native shape.
 *
 * SUBTITLES ARE NOT OFFERED. The files here carry them burned into the
 * picture where they are wanted, so a chooser would mean either converting
 * something nobody selects or — for image subtitles — quietly starting a
 * video transcode on the house server.
 *
 * WHY AUDIO IS A SERVER CALL AND SPEED IS NOT. The television direct-plays
 * the file and ExoPlayer demuxes it, so every track inside is available.
 * Chrome exposes no way to change which audio stream a file plays at all, so
 * picking one asks the server again and it applies the choice upstream — a
 * real transcode. Speed is a property of the media element and costs nothing.
 * The menu says which is which.
 */

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export function PlayerOptions() {
  const { item, tracks, rate } = usePlayer();
  const [open, setOpen] = useState<null | 'audio' | 'speed'>(null);
  const [working, setWorking] = useState(false);

  /* Only a library item has tracks. A channel has one of everything and no
     timeline, so every control here would be inert furniture. */
  if (!item) return null;

  const audio = tracks?.audio ?? [];

  const pickAudio = async (id: number) => {
    setOpen(null);
    setWorking(true);
    try { await player.selectAudio(id); } finally { setWorking(false); }
  };

  return (
    <div className="popts">
      {/* One audio track is not a choice, and a menu with a single item in it
          is a control that exists to be disappointing. */}
      {audio.length > 1 && (
        <Menu
          open={open === 'audio'}
          onOpen={() => setOpen(open === 'audio' ? null : 'audio')}
          onClose={() => setOpen(null)}
          label="Audio track"
          busy={working}
          face="Audio"
        >
          {audio.map((t) => (
            <Choice
              key={t.id}
              on={tracks?.audioStreamId === t.id || (tracks?.audioStreamId == null && t.selected)}
              onPick={() => pickAudio(t.id)}
              /* THE COST, SAID BEFORE IT IS PAID. There is no sidecar trick
                 for sound — anything but the default goes through Plex's
                 transcoder, on a box that is also serving everybody else. */
              note={t.selected ? undefined : 're-encodes'}
            >
              {label(t)}
            </Choice>
          ))}
        </Menu>
      )}

      <Menu
        open={open === 'speed'}
        onOpen={() => setOpen(open === 'speed' ? null : 'speed')}
        onClose={() => setOpen(null)}
        label="Playback speed"
        active={rate !== 1}
        face={rate === 1 ? '1×' : `${rate}×`}
      >
        {SPEEDS.map((r) => (
          <Choice key={r} on={rate === r} onPick={() => { setOpen(null); player.setRate(r); }}>
            {r === 1 ? 'Normal' : `${r}×`}
          </Choice>
        ))}
      </Menu>
    </div>
  );
}

/** Plex's own wording, minus the noise nobody reads. */
function label(t: AudioTrack): string {
  const text = t.label || t.language || `Track ${t.id}`;
  return t.forced ? `${text} (forced)` : text;
}

function Menu({
  open, onOpen, onClose, label, face, active, busy, children,
}: {
  open: boolean;
  onOpen(): void;
  onClose(): void;
  label: string;
  face: string;
  active?: boolean;
  busy?: boolean;
  children: React.ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);

  /*
   * CLOSES ON ANYTHING ELSE. A popover that only closes by pressing its own
   * button is a popover people leave open, and this one sits over a video.
   * Pointerdown rather than click so it closes on the press that begins an
   * interaction elsewhere rather than on the release.
   */
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open, onClose]);

  return (
    <div className="popt" ref={box}>
      <button
        type="button"
        className={`popt-btn${active ? ' is-on' : ''}`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={onOpen}
      >
        {face}
      </button>
      {open && <div className="popt-menu" role="menu">{children}</div>}
    </div>
  );
}

function Choice({
  on, onPick, note, children,
}: {
  on: boolean;
  onPick(): void;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <button type="button" role="menuitemradio" aria-checked={on} className="popt-item" onClick={onPick}>
      <span className="popt-tick">{on ? '✓' : ''}</span>
      <span className="popt-label">{children}</span>
      {note && <span className="popt-cost">{note}</span>}
    </button>
  );
}
